/**
 * The catalogue behind the guide (see Modals/GuideModal.tsx).
 *
 * The guide does not annotate the live game. It opens a modal holding a
 * miniature, entirely dead copy of the game screen — the real components, in
 * their own store, against an invented game (Components/GuideGameMock.tsx and
 * utils/guideMockGame.ts) — and rings the parts of *that*. So a player can read
 * it mid-turn without a card landing under a label, without the board being
 * greyed out while they read, and without the tour having to survive a sidebar
 * rearranging itself underneath it.
 *
 * Every entry here is a piece of text plus a hint about which way its pointer
 * should lean. Which element it points at is *not* here: the copy is made of
 * the game's own components, so the selectors live beside the game state that
 * decides them, in utils/guideMockGame.ts. A spot whose element is not on
 * screen in the mock is quietly left off the diagram.
 *
 * The copy holds a good deal more than this list labels, on purpose. A label is
 * only worth its clutter where the thing it points at cannot be worked out by
 * looking at it: the turn buttons say what they do on their faces, a dropped
 * player's card already reads "Disconnected — bot takes over in 38s", and a
 * fullscreen key is a fullscreen key. Those stay on the diagram, unlabelled,
 * because a diagram missing half the screen is not a diagram of the screen —
 * they are simply not stops on the tour. What is labelled here is what this
 * game does that a player has no way of guessing: the house rules, the piles,
 * the clocks and the panels.
 *
 * `side`/`lane`/`align` are only a starting position. The modal resolves
 * collisions by pushing labels further out along their own axis, so a hint that
 * turns out to be crowded degrades into a slightly longer pointer rather than
 * into two labels on top of each other.
 *
 * Descriptions are about *this* game, not the boxed one it resembles. The
 * auction-by-default rule, the steal-tax pile and the turn limit are all house
 * rules a new player has no reason to expect, and those are exactly the ones
 * worth spending words on. Mortgaging and hotels exist in the code but are not
 * part of the game, so they are deliberately absent.
 */

export type HelpSide = "top" | "bottom" | "left" | "right"

export type HelpGroupId = "board" | "info" | "players" | "controls"

export interface IHelpGroup {
  id: HelpGroupId
  label: string
  icon: string
  /** One line under the tabs, naming what this group is about. */
  blurb: string
}

export const helpGroups: IHelpGroup[] = [
  {
    id: "board",
    label: "Board tiles",
    icon: "fa-chess-board",
    blurb: "The kinds of tile your piece can land on, and what each one does to you.",
  },
  {
    id: "info",
    label: "Board centre",
    icon: "fa-circle-info",
    blurb:
      "The clocks, counters and piles in the middle of the board that tell you where the game is.",
  },
  {
    id: "players",
    label: "Players & panels",
    icon: "fa-users",
    blurb: "The two side columns: who is playing, what they own, and the deals on the table.",
  },
  {
    id: "controls",
    label: "Controls",
    icon: "fa-sliders",
    blurb:
      "The keys in the top-right corner. None of them change the game — they change your screen.",
  },
]

export interface IHelpSpot {
  /** Keys this spot's selector in utils/guideMockGame.ts. */
  id: string
  /** Short name on the pointer chip. Two or three words — it has to fit beside
   *  a board tile without covering its neighbour. */
  label: string
  /** What it is and what it does. One or two sentences. */
  description: string
  /** The non-obvious bit — the thing new players get wrong. */
  tip?: string
  group: HelpGroupId
  icon: string

  /** Which way the label leans off the element. A hint: the modal pushes it
   *  further along this axis if the place it wants is already taken. */
  side: HelpSide
  /** Starts the label this many steps further out along `side`, for spots whose
   *  neighbours are known to be tight (the turn buttons, the HUD keys). */
  lane?: number
  /** How a top/bottom label sits against the point the pointer starts from. */
  align?: "start" | "center" | "end"
}

export const helpSpots: IHelpSpot[] = [
  // ---------------------------------------------------------------------
  // Board tiles. Their labels lean outward, off the edge of the board, so the
  // pointers never have to cross the middle of it.
  // ---------------------------------------------------------------------
  {
    id: "go-tile",
    label: "Start",
    description:
      "The corner every lap starts from. Passing it pays you a salary, and it is also the only place the Stock Exchange opens: you can put spare cash in, or take an earlier stake back out with whatever it has earned.",
    tip: "Stock grows 30% a lap — 60% as the Wall Street Wolf — and stops growing after two laps. Cash it in on the lap it tops out; money sitting in stock is money you cannot bid with.",
    group: "board",
    icon: "fa-flag",
    side: "left",
  },
  {
    id: "owner-marker",
    label: "Owner marker",
    description:
      "The faded piece printed on a property tile is its owner. That is who you pay when you land there.",
    group: "board",
    icon: "fa-user-tag",
    // Leans out over the sidebar rather than across the board: the marker is
    // printed down the middle of a tile in one of the side columns, and a label
    // hung above or below it would land on the tile's neighbours.
    side: "left",
  },
  {
    id: "house-marker",
    label: "Houses",
    description:
      "Houses standing on a property. Each one raises the rent that tile charges, steeply.",
    group: "board",
    icon: "fa-house-chimney",
    side: "bottom",
  },
  {
    id: "steal-tax-tile",
    label: "Steal Tax",
    description:
      "The single best tile on the board. Land on it and you collect the entire tax pile that everyone else has been paying into — the whole lot, in one go.",
    tip: "Check the size of the pile before you spend a card that moves you. This tile swings games.",
    group: "board",
    icon: "fa-sack-dollar",
    side: "right",
  },
  {
    id: "decision-hub-tile",
    label: "Decision Hub",
    description:
      "Land here and you pick your own reward: take the cash, or draw action cards instead.",
    tip: "Cash is safe, cards are leverage. Take cards when you are behind and need something to swing with.",
    group: "board",
    icon: "fa-code-branch",
    // The hub the guide rings is the one in the board's top corner, so the
    // label goes up and out into the empty strip above the board rather than
    // down the side, where the sidebar labels already are.
    side: "top",
  },

  // ---------------------------------------------------------------------
  // The middle of the board.
  // ---------------------------------------------------------------------
  {
    id: "turn-timer",
    label: "Turn timer",
    description:
      "How long the player whose turn it is has left. It runs down for whoever is playing, not just for you.",
    tip: "It ticks out loud on your own turn only, so you can look away without losing it.",
    group: "info",
    icon: "fa-stopwatch",
    side: "right",
  },
  {
    id: "turns-left",
    label: "Turns left",
    description:
      "How many full rounds the game has left — one round is every player rolling once, not one turn of yours.",
    tip: "At zero the game ends and the highest net worth wins: cash plus properties plus houses.",
    group: "info",
    icon: "fa-hourglass-half",
    side: "right",
    lane: 1,
  },
  {
    id: "money-on-table",
    label: "The tax pile",
    description:
      "Every tax anyone pays lands here instead of vanishing, and it sits on the table until somebody takes it.",
    tip: "This is what the Steal Tax tile pays out. Watch it grow — late on it can be worth more than a colour set.",
    group: "info",
    icon: "fa-coins",
    side: "right",
  },
  {
    id: "idle-countdown",
    label: "Idle countdown",
    description:
      "Someone has stopped responding, and the game is about to roll or end the turn for them so play can carry on.",
    tip: "Any input from that player cancels it. It turns red for the last few seconds.",
    group: "info",
    icon: "fa-hourglass-end",
    side: "bottom",
  },

  // ---------------------------------------------------------------------
  // The two side columns.
  // ---------------------------------------------------------------------
  {
    id: "fast-pace",
    label: "Fast pace",
    description:
      "Puts a vote to the table to speed the game up: shorter timers, quicker animations. While the vote is open a panel stands here in its place, with everyone's answers on it; once it passes it becomes a badge saying the game is fast paced.",
    tip: "It needs the whole table to agree, and the switch is one-way — once they do, the game stays fast for the rest of it.",
    group: "players",
    icon: "fa-bolt",
    side: "right",
  },
  {
    id: "role-badge",
    label: "Role badge",
    description:
      "Every player has a secret-weapon role with a permanent perk — extra rent, cheaper builds, buying without an auction, and so on.",
    tip: "Hover any badge, including your opponents', to read exactly what that role does. Knowing a rival is a Rent Lord changes which properties you let them have.",
    group: "players",
    icon: "fa-star",
    side: "right",
    lane: 1,
  },
  {
    id: "last-auction",
    label: "Last auction",
    description: "Re-opens the results of the most recent auction: who bid what, and who won.",
    tip: "Worth a look every time. What your rivals were willing to pay tells you how much cash they are sitting on.",
    group: "players",
    icon: "fa-gavel",
    side: "right",
  },
  {
    id: "create-trade",
    label: "Create Trade",
    description:
      "Offers a deal to any player: properties and cash, in either direction. Trading is how sets actually get completed.",
    tip: "Available all game, on anybody's turn — you never have to wait for your own turn to make a deal.",
    group: "players",
    icon: "fa-handshake",
    side: "right",
  },
  {
    id: "bank",
    label: "Bank",
    description:
      "Every property nobody has bought yet. Once a tile leaves here it never comes back.",
    tip: "Check what is left before you overbid at an auction. If two tiles of a set are still in the bank, you have time.",
    group: "players",
    icon: "fa-landmark",
    side: "right",
  },
  {
    id: "trades-panel",
    label: "Active trades",
    description:
      "Every offer currently on the table, plus the ones that just finished. Offers here are on a clock.",
    group: "players",
    icon: "fa-arrow-right-arrow-left",
    side: "left",
  },

  // ---------------------------------------------------------------------
  // The HUD keys, top-right — the two that are worth a word. The rest of the
  // cluster says what it is on its own.
  //
  // Both lean *up*, into the empty strip above the screen, and on different
  // lanes. These keys sit shoulder to shoulder in one row, so a label leaning
  // left or right off either of them lands squarely on the key next door — the
  // solver keeps labels off each other and off the things being pointed at, but
  // it cannot invent room that the row does not have.
  // ---------------------------------------------------------------------
  {
    id: "music-toggle",
    label: "Music",
    description: "Turns the background music on and off. Game sounds are separate, in Settings.",
    group: "controls",
    icon: "fa-music",
    side: "top",
  },
  {
    id: "settings-button",
    label: "Settings",
    description: "Sound, board background and the rest of your in-game preferences.",
    group: "controls",
    icon: "fa-gear",
    side: "top",
    lane: 1,
  },
]
